<template>
  <Panel flat>
    <div class="dashboard-reports">
      <div class="dashboard-reports__header">
        <div class="dashboard-reports__title typo-small-title">
          {{ $t(`operations.site_health_reports_${identifier}`) }}
        </div>
        <router-link
          v-if="createPolicy && !loading"
          class="dashboard-reports__button"
          :to="{ name: newReportRoute }"
        >
          <PlusIcon class="icon icon--small" />
        </router-link>
      </div>

      <div class="dashboard-reports__content">
        <Loader
          v-if="loading"
          class="dashboard-reports__loader"
        />
        <DataFetchError v-if="!loading & fetchError" />
        <div
          v-if="!loading"
          class="dashboard-reports__list"
        >
          <div
            v-if="reports && reports.length === 0 & !fetchError"
            class="dashboard-reports__no-reports typo-tiny"
          >
            {{ $t('operations.no_reports') }}
          </div>
          <div
            v-for="report in reports"
            :key="report.id"
            class="dashboard-report"
          >
            <div class="dashboard-report__icon">
              <FileDocIcon class="icon icon--large" />
            </div>
            <div class="dashboard-report__name typo-small-title">
              <span>{{ report.title }}</span>
            </div>
            <div
              v-if="report?.download_size"
              class="dashboard-report__size typo-tiny"
            >
              {{ report.download_size }}
            </div>
            <div class="dashboard-report__creator typo-tiny">
              <span>{{ createdBy(report) }}</span>
            </div>
            <div class="dashboard-report__status typo-tiny">
              <span :class="`dashboard-report__status-label`">
                {{ $t(`site_health_report.statuses.${report.status}`) }}
              </span>
            </div>
            <div class="dashboard-report__menu">
              <Dropdown
                :items="reportMenuItems(report)"
                @click-item="onReportItemAction(report.id, $event)"
              >
                <EllipsisIcon class="icon" />
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Panel>
  <Dialog
    v-if="dialogIsOpen"
    :message="I18n.t('issues.confirm_delete_message')"
    :title="I18n.t('issues.confirm_delete_title', { type: I18n.t(`issues.type_report`) })"
    @dismiss-dialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="onReportItem"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import I18n from 'i18n-js';

// Utils
import { formatDate } from '@/functions/date_format_helpers';

// Components
import DataFetchError from '@/components/errors/data_fetch_error.vue';
import Dialog from '@/components/generic/dialog.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import Loader from '@/components/generic/loader.vue';
import Panel from '@/components/dashboard/panel.vue';

// Icons
import EllipsisIcon from '@/images/vertical-ellipsis.svg';
import FileDocIcon from '@/images/file-doc.svg';
import PlusIcon from '@/images/plus-16.svg';

const emit = defineEmits(['deleteReport']);

const props = defineProps({
  /** Shows/hides new report button based on the create policy */
  createPolicy: { type: Boolean },
  /** The current loading state */
  loading: { type: Boolean, default: false },
  /** An array of reports. An empty array will render the empty state */
  reports: { type: Array, default: () => [] },
  /** Triggers the DataFetchError component */
  fetchError: { type: Boolean, required: false, default: false },
  /**
   * Current loading state of the incoming data,
   * Expects `plant`, `shaft` or `ship`. This sets the title and button url
   */
  identifier: {
    type: String,
    required: true,
    default: 'plant',
    validator(value) {
      return ['plant', 'ship', 'shaft', 'equipment'].includes(value);
    }
  },
});

const deletionId = ref(null);
const dialogAction = ref(null);
const dialogIsOpen = ref(false);

const newReportRoute = computed(() => {
  switch (props.identifier) {
    case 'shaft':
      return 'newShaftV2HealthReport';
    case 'ship':
      return 'newShipHealthReport';
    case 'equipment':
      return 'newEquipmentHealthReport';
    default:
      return 'newSiteHealthReport';
  }
});

/**
 * Sets the correct name & params for the "Edit" dropdown menu item
 */
function editReportRoute(report) {
  switch (props.identifier) {
    case 'shaft':
      return {
        name: 'editShaftV2HealthReport',
        params: { shaftHealthReportId: report.id },
      };
    case 'ship':
      return {
        name: 'editShipHealthReport',
        params: { shipHealthReportId: report.id },
      };
    case 'equipment':
      return {
        name: 'editEquipmentHealthReport',
        params: { equipmentHealthReportId: report.id },
      };
    default:
      return {
        name: 'editSiteHealthReport',
        params: { siteHealthReportId: report.id },
      };
  }
}

/**
 * Sets the correct name & params for the "use as template" dropdown menu item
 */
function useAsTemplateRoute(report) {
  switch (props.identifier) {
    case 'shaft':
      return {
        name: 'newShaftV2HealthReport',
        params: { shaftHealthReportTemplateId: report.id },
      };
    case 'ship':
      return {
        name: 'newShipHealthReport',
        params: { shipHealthReportTemplateId: report.id },
      };
    case 'equipment':
      return {
        name: 'newEquipmentHealthReport',
        params: { equipmentHealthReportTemplateId: report.id },
      };
    default:
      return {
        name: 'newSiteHealthReport',
        params: { siteHealthReportTemplateId: report.id },
      };
  }
}

function reportMenuItems(report) {
  return [
    {
      label: I18n.t('general.edit'),
      to: editReportRoute(report),
      modifiers: [!report.policy.edit ? 'disabled' : null],
    },
    {
      label: I18n.t('site_health_report.use_as_template'),
      to: useAsTemplateRoute(report),
      modifiers: [!(props.createPolicy) ? 'disabled' : null],
    },
    {
      label: I18n.t('general.download'),
      path: report.download_url,
      target: '_blank',
      modifiers: [
        report.status === 'error' || report.status === 'generating'
          ? 'disabled'
          : null,
      ],
    },
    {
      label: I18n.t('general.delete'),
      key: 'delete',
      modifiers: ['destructive', !report.policy.destroy ? 'disabled' : null],
    },
  ];
}

function createdBy(report) {
  return `${I18n.t('site_health_report.updated')} ${formatDate(
    report.updated_at
  )} ${I18n.t('site_health_report.by')} ${report.created_by.full_name}`;
}

/**
 * Toggles dialog,
 * stores the id used for deletion,
 * stores the key form the dropdown menu item
 */
function onReportItemAction(id, { key }) {
  if (key === 'delete') {
    dialogIsOpen.value = true;
    deletionId.value = id;
    dialogAction.value = key;
  }
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  deletionId.value = null;
  dialogAction.value = null;
}

async function onReportItem() {
  emit('deleteReport', deletionId.value);

  dismissDialogAction();
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.dashboard-reports {
  @include breakpoint(medium) {
    margin-bottom: 0;
    grid-column: 1;
  }

  &__header {
    padding: 0 bu(4);
    border-bottom: 1px solid map-get($background-color, default);
    display: flex;
    align-items: center;
    height: bu(6);
  }

  &__title {
    margin-right: auto;
  }

  &__content {
    padding-bottom: bu(2);
  }

  &__no-reports {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-around;
    height: rem(70);
    font-style: italic;
    color: map-get($foreground-color, dark);
    border-bottom: 1px solid map-get($background-color, default);

    @include breakpoint(large) {
      height: rem(80);
    }
  }

  &__loader {
    height: rem(70);
    border-bottom: 1px solid map-get($background-color, default);

    @include breakpoint(large) {
      height: rem(80);
    }
  }
}

.dashboard-report {
  display: grid;
  grid-template:
    'icon name name    name   menu' auto
    'icon size creator status menu' auto / auto auto auto 1fr auto;
  padding: bu(1.5);
  border-bottom: 1px solid map-get($background-color, default);

  @include breakpoint(large) {
    grid-template-columns: min-content 3fr bu(10) minmax(20%, 1fr) bu(10) auto;
    grid-template-areas: 'icon name size creator status menu';
    padding: 0;

    &:hover {
      background-color: map-get($background-color, lightest);
    }
  }

  &__icon,
  &__name,
  &__size,
  &__creator,
  &__status,
  &__menu {
    @include breakpoint(large) {
      padding: bu(2.5) bu(3);
      vertical-align: middle;
    }
  }

  &__size,
  &__status {
    @include breakpoint(large) {
      white-space: nowrap;
    }
  }

  &__creator {
    @include breakpoint(large) {
      span {
        @include line-clamp(3);
      }
    }
  }

  &__status-label {
    border-radius: map-get($border-radius, medium);
    border: solid 1px transparentize(map-get($foreground-color, light), 0.94);
    background-color: transparentize(map-get($foreground-color, light), 0.93);
    padding: bu(0.5);

    &--error {
      color: map-get($error-color, light);
    }
  }

  &__icon {
    grid-area: icon;
    margin-right: bu(1.5);

    @include breakpoint(large) {
      margin-right: 0;
    }
  }

  &__name {
    grid-area: name;
    margin-bottom: bu(1);

    @include breakpoint(large) {
      margin-bottom: 0;
      width: 100%;
    }

    span {
      @include line-clamp(2);
    }
  }

  &__size,
  &__creator {
    &:after {
      content: '•';
      color: map-get($foreground-color, dark);
      margin: 0 0.5rem;

      @include breakpoint(large) {
        content: none;
      }
    }
  }

  &__size {
    grid-area: size;
  }

  &__creator {
    grid-area: creator;
  }

  &__status {
    grid-area: status;
  }

  &__menu {
    grid-area: menu;

    @include breakpoint(large) {
      text-align: right;

      svg {
        display: inline-block;
        visibility: hidden;
      }
    }
  }

  &:hover &__menu svg {
    visibility: visible;
  }
}
</style>
